import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, GeoJSON, LayersControl, useMap, Circle, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MEGATHRUST_ZONES, VOLCANOES, VOLCANO_ALERTS, nearestCity } from "../lib/api";

const depthColor = (d) => (d < 50 ? "#ef4444" : d <= 300 ? "#f97316" : "#10b981");
const magRadius = (m) => Math.max(4, m * 3.5);

// FRP → color/intensity (Sipongi+ risk classification)
const fireStyle = (fp) => {
  const frp = fp.frp || 0;
  const highConf = fp.confidence === "high";
  if (frp > 50 || highConf) return { color: "#dc2626", risk: "Tinggi", size: 22 };
  if (frp >= 20) return { color: "#f97316", risk: "Sedang", size: 18 };
  return { color: "#facc15", risk: "Ringan", size: 15 };
};

// Lucide Flame SVG path — no glow filter, kept lightweight for perf
const flameIcon = (color, size) => {
  const html = `
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="#0f172a" stroke-width="1" stroke-linejoin="round">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
    </svg>`;
  return L.divIcon({
    html,
    className: "pantau-fire-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const volcanoIcon = (v) => {
  const color = (VOLCANO_ALERTS[v.alertLevel] || VOLCANO_ALERTS[1]).color;
  const size = 14 + (v.alertLevel || 1) * 2;
  const html = `
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" style="filter: drop-shadow(0 0 4px ${color}aa)">
      <polygon points="12,3 22,21 2,21" fill="${color}" stroke="#0f172a" stroke-width="1.5" stroke-linejoin="round"/>
      <polygon points="12,8 17,19 7,19" fill="#0f172a" opacity="0.25"/>
    </svg>`;
  return L.divIcon({
    html,
    className: "pantau-volcano-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size - 2],
  });
};

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1.2 });
  }, [center, zoom, map]);
  return null;
}

export default function Map2D({ quakes, region, layers, wildfires, geoPlates, focusQuake, onSelect, accent, volcanoes = VOLCANOES, onPressRelease }) {
  const now = Date.now();
  const mapRef = useRef();

  useEffect(() => {
    if (focusQuake && mapRef.current) {
      mapRef.current.flyTo([focusQuake.latitude, focusQuake.longitude], 6, { duration: 1.4 });
    }
  }, [focusQuake]);

  return (
    <div className="h-full w-full" data-testid="map-2d-container">
    <MapContainer
      ref={mapRef}
      center={region.center}
      zoom={region.zoom}
      className="h-full w-full"
      zoomControl={true}
      worldCopyJump
      preferCanvas
    >
      <Recenter center={region.center} zoom={region.zoom} />
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="OpenStreetMap">
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Dark (OSM)">
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="leaflet-dark-tiles"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite (Esri)">
          <TileLayer
            attribution='&copy; Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Terrain (OpenTopo)">
          <TileLayer
            attribution='&copy; OpenTopoMap (CC-BY-SA)'
            url="https://a.tile.opentopomap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
      </LayersControl>

      {/* Tectonic plates */}
      {layers.plates && geoPlates && (
        <GeoJSON data={geoPlates} style={{ color: "#38bdf8", weight: 1.2, opacity: 0.6, fillOpacity: 0 }} />
      )}

      {/* Megathrust faults */}
      {layers.faults && MEGATHRUST_ZONES.map((z) => (
        <Polyline
          key={z.name}
          positions={z.coords}
          pathOptions={{ color: "#dc2626", weight: 2.5, dashArray: "6 6", opacity: 0.85 }}
        />
      ))}

      {/* Volcanoes as triangles, color intensifies with alert level */}
      {layers.volcanoes && volcanoes.map((v) => {
        const alert = VOLCANO_ALERTS[v.alertLevel] || VOLCANO_ALERTS[1];
        return (
          <Marker key={v.id} position={[v.latitude, v.longitude]} icon={volcanoIcon(v)}>
            <Popup>
              <div className="text-slate-900 text-xs">
                <div className="font-bold">{v.name}</div>
                <div>{v.country} · {v.type}</div>
                <div className="font-mono">Elev: {v.elevation} m</div>
                <div className="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: alert.color }}>
                  Level {v.alertLevel} · {alert.label}{v.alertLive ? " · PVMBG live" : ""}
                </div>
                {onPressRelease && (
                  <button
                    onClick={() => onPressRelease("volcano", v)}
                    className="mt-2 block w-full rounded bg-cyan-500 px-2 py-1 text-[10px] font-bold text-slate-950 hover:bg-cyan-400"
                    data-testid={`pr-volcano-${v.id}`}
                  >📰 Buat Naskah Berita</button>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Wildfires — Flame icons (top 400 by FRP for perf) */}
      {layers.wildfires && wildfires.slice(0, 400).map((w) => {
        const s = fireStyle(w);
        const near = nearestCity(w.latitude, w.longitude);
        return (
          <Marker
            key={w.id}
            position={[w.latitude, w.longitude]}
            icon={flameIcon(s.color, s.size)}
          >
            <Popup>
              <div className="text-slate-900 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={s.color}><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>
                  <div>
                    <div className="font-bold">{w.source === "FIRMS" ? "Titik Panas FIRMS" : "Kebakaran EONET"}</div>
                    <div className="text-[10px] opacity-70">{w.instrument || w.source} · {w.date}</div>
                  </div>
                </div>
                {w.frp != null && (
                  <div className="font-mono">FRP: <b>{w.frp} MW</b> · Suhu: {w.brightness} K</div>
                )}
                {w.confidence && <div>Confidence: <b className="uppercase">{w.confidence}</b></div>}
                {w.daynight && <div>Pass: {w.daynight === "D" ? "Day" : "Night"} · Sat: {w.satellite}</div>}
                <div className="font-mono text-[11px]">{w.latitude.toFixed(4)}, {w.longitude.toFixed(4)}</div>
                {near && <div className="text-[11px]">Kota terdekat: <b>{near.name}</b> · {near.distanceKm.toFixed(0)} km</div>}
                <div className="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: s.color }}>Risiko {s.risk}</div>
                {onPressRelease && (
                  <button
                    onClick={() => onPressRelease("fire", w)}
                    className="mt-2 block w-full rounded bg-cyan-500 px-2 py-1 text-[10px] font-bold text-slate-950 hover:bg-cyan-400"
                    data-testid={`pr-fire-${w.id}`}
                  >📰 Buat Naskah Berita</button>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Earthquakes */}
      {quakes.map((q) => {
        const fresh = now - q.time < 3 * 3600 * 1000;
        const color = depthColor(q.depth);
        return (
          <React.Fragment key={q.id}>
            {fresh && (
              <Circle
                center={[q.latitude, q.longitude]}
                radius={magRadius(q.magnitude) * 15000}
                pathOptions={{ color, weight: 1, opacity: 0.6, fillOpacity: 0, className: "leaflet-radar" }}
              />
            )}
            <CircleMarker
              center={[q.latitude, q.longitude]}
              radius={magRadius(q.magnitude)}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.6, weight: 1.5 }}
              eventHandlers={{ click: () => onSelect && onSelect(q) }}
            >
              <Popup>
                <div className="text-slate-900 text-xs space-y-0.5">
                  <div className="font-bold text-sm">M {q.magnitude?.toFixed(1)}</div>
                  <div>{q.location}</div>
                  <div className="font-mono">Depth: {q.depth?.toFixed(1)} km</div>
                  <div className="font-mono">{new Date(q.time).toLocaleString()}</div>
                  <div className="font-mono text-[10px] opacity-70">Source: {q.source}</div>
                </div>
              </Popup>
            </CircleMarker>
          </React.Fragment>
        );
      })}
    </MapContainer>
    </div>
  );
}
